import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, HostBinding } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../services/auth.service';
import { RoleGroups, RoleNames } from '../../../constants/role-names';

interface NavItem {
  label: string;
  icon: string;
  url: string;
  /** Optional narrower role list than the group's. */
  roles?: string[];
}

interface NavGroup {
  key: string;
  label: string;
  icon: string;
  roles: string[];
  items: NavItem[];
}

/*
 * Keep these role lists in sync with `data.role` in pages/dashboard/dashboard-routing.module.ts.
 */
@Component({
  selector: 'app-sidebar',
  templateUrl: './app-sidebar.component.html',
  styleUrls: ['./app-sidebar.component.scss'],
})
export class AppSidebarComponent implements OnInit, OnDestroy {
  @Input() mobileOpen = false;
  @Output() close = new EventEmitter<void>();

  @HostBinding('class.sidebar-collapsed') get collapsedClass() { return this.isCollapsed; }
  @HostBinding('class.sidebar-mobile-open') get mobileOpenClass() { return this.mobileOpen; }

  isCollapsed = false;
  activeGroup: string | null = null;
  currentUrl = '';

  private _destroy$ = new Subject<void>();

  readonly navGroups: NavGroup[] = [
    {
      key: 'overview', label: 'Overview', icon: 'ph-squares-four',
      roles: RoleGroups.Overview,
      items: [
        { label: 'Analytics',    icon: 'ph-chart-bar',           url: '/dashboard/home' },
        { label: 'Realtime',     icon: 'ph-heartbeat',           url: '/dashboard/realtime' },
        { label: 'Tracking',     icon: 'ph-map-pin',             url: '/dashboard/tracking' },
      ],
    },
    {
      key: 'atm', label: 'ATM Management', icon: 'ph-device-mobile',
      roles: RoleGroups.AdminManagerSupervisor,
      items: [
        { label: 'ATMs',         icon: 'ph-device-mobile',       url: '/dashboard/atms' },
        { label: 'ATM Logs',     icon: 'ph-warning-circle',      url: '/dashboard/atm-logs' },
        { label: 'KYC',          icon: 'ph-identification-card', url: '/dashboard/kycs' },
        { label: 'Images',       icon: 'ph-image',               url: '/dashboard/images' },
      ],
    },
    {
      key: 'transactions', label: 'Transactions', icon: 'ph-arrows-left-right',
      roles: [...RoleGroups.AdminManagerSupervisor, RoleNames.Staff],
      items: [
        { label: 'Transactions',    icon: 'ph-arrows-left-right', url: '/dashboard/transactions' },
        { label: 'Crypto Txns',     icon: 'ph-currency-btc',      url: '/dashboard/crypto-transactions' },
        { label: 'Commission Txns', icon: 'ph-percent',           url: '/dashboard/commission-transactions', roles: [RoleNames.Administrator] },
      ],
    },
    {
      key: 'finance', label: 'Finance', icon: 'ph-currency-circle-dollar',
      roles: [RoleNames.Administrator],
      items: [
        { label: 'Commission Config', icon: 'ph-sliders',        url: '/dashboard/commission-config' },
        { label: 'Withdrawals',       icon: 'ph-bank',           url: '/dashboard/withdrawal-requests' },
      ],
    },
    {
      key: 'partner', label: 'My Portal', icon: 'ph-handshake',
      roles: [RoleNames.Partner],
      items: [
        { label: 'Dashboard',    icon: 'ph-chart-bar',           url: '/dashboard/partner/home' },
        { label: 'Transactions', icon: 'ph-arrows-left-right',   url: '/dashboard/partner/transactions' },
        { label: 'ATMs',         icon: 'ph-device-mobile',       url: '/dashboard/partner/atms' },
        { label: 'Staff',        icon: 'ph-user',                url: '/dashboard/partner/staff' },
        { label: 'KYCs',         icon: 'ph-identification-card', url: '/dashboard/partner/kycs' },
        { label: 'ATM Logs',     icon: 'ph-warning-circle',      url: '/dashboard/partner/atm-logs' },
      ],
    },
    {
      key: 'partner-finance', label: 'Finance', icon: 'ph-currency-circle-dollar',
      roles: [RoleNames.Partner],
      items: [
        { label: 'Investors',                    icon: 'ph-briefcase',     url: '/dashboard/investors' },
        { label: 'Investor withdrawal requests', icon: 'ph-hand-coins',    url: '/dashboard/withdrawal-requests' },
        { label: 'Commission rates',             icon: 'ph-sliders',       url: '/dashboard/commission-config' },
        { label: 'Commission transactions',      icon: 'ph-percent',       url: '/dashboard/commission-transactions' },
        { label: 'My payouts',                   icon: 'ph-bank',          url: '/dashboard/partner/withdrawals' },
      ],
    },
    {
      key: 'operations', label: 'Operations', icon: 'ph-wrench',
      roles: RoleGroups.Operations,
      items: [
        { label: 'Tickets',       icon: 'ph-ticket',             url: '/dashboard/tickets' },
        { label: 'Notifications', icon: 'ph-bell',               url: '/dashboard/notifications' },
      ],
    },
    {
      key: 'crypto', label: 'Crypto', icon: 'ph-currency-btc',
      roles: RoleGroups.AdminManagerSupervisor,
      items: [
        { label: 'Networks', icon: 'ph-share-network',           url: '/dashboard/networks' },
        { label: 'Tokens',   icon: 'ph-coin',                   url: '/dashboard/tokens' },
      ],
    },
    {
      key: 'admin', label: 'Admin', icon: 'ph-shield-check',
      roles: RoleGroups.AdminManager,
      items: [
        { label: 'Partners',      icon: 'ph-handshake',          url: '/dashboard/partners',       roles: [RoleNames.Administrator] },
        { label: 'Investors',     icon: 'ph-briefcase',          url: '/dashboard/investors',      roles: [RoleNames.Administrator] },
        { label: 'Partner Staff', icon: 'ph-users',              url: '/dashboard/partner-staffs', roles: [RoleNames.Administrator] },
        { label: 'Staff',         icon: 'ph-user',               url: '/dashboard/staffs' },
        { label: 'Country',       icon: 'ph-globe',              url: '/dashboard/country',        roles: [RoleNames.Administrator] },
        { label: 'Regions',       icon: 'ph-map',                url: '/dashboard/regions',        roles: [RoleNames.Administrator] },
        { label: 'Banks',         icon: 'ph-bank',               url: '/dashboard/banks' },
        { label: 'IP Allowlist',  icon: 'ph-network',            url: '/dashboard/ip-allowlist', roles: [RoleNames.Administrator] },
        { label: 'Security',      icon: 'ph-lock-key',           url: '/dashboard/security',     roles: [RoleNames.Administrator] },
      ],
    },
    {
      key: 'investor', label: 'My Portal', icon: 'ph-briefcase',
      roles: [RoleNames.Investor],
      items: [
        { label: 'Dashboard',    icon: 'ph-chart-bar',           url: '/dashboard/investor/home' },
        { label: 'Transactions', icon: 'ph-arrows-left-right',   url: '/dashboard/investor/transactions' },
      ],
    },
    {
      key: 'investor-profit', label: 'Investor & Profit Sharing', icon: 'ph-hand-coins',
      roles: [RoleNames.Investor],
      items: [
        { label: 'Withdrawals',  icon: 'ph-bank',                url: '/dashboard/investor/withdrawals' },
      ],
    },
  ];

  constructor(
    private router: Router,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.currentUrl = this.router.url;
    this.openActiveGroup();

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this._destroy$))
      .subscribe((e: NavigationEnd) => {
        this.currentUrl = e.urlAfterRedirects;
        this.openActiveGroup();
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get visibleGroups(): NavGroup[] {
    return this.navGroups
      .filter(g => this.authService.isRole(g.roles))
      .map(g => ({ ...g, items: g.items.filter(i => !i.roles || this.authService.isRole(i.roles)) }))
      .filter(g => g.items.length > 0);
  }

  get currentUser() {
    return this.authService.currentUser;
  }

  get userInitials(): string {
    const u = this.currentUser;
    if (!u) { return '?'; }
    return ((u.firstName?.[0] ?? '') + (u.lastName?.[0] ?? '')).toUpperCase() || '?';
  }

  logout(): void {
    this.authService.logout();
  }

  toggleGroup(key: string): void {
    this.activeGroup = this.activeGroup === key ? null : key;
  }

  isItemActive(url: string): boolean {
    return this.currentUrl === url || this.currentUrl.startsWith(url + '/') || this.currentUrl.startsWith(url + '?');
  }

  isGroupActive(group: NavGroup): boolean {
    return group.items.some(item => this.isItemActive(item.url));
  }

  private openActiveGroup(): void {
    const active = this.visibleGroups.find(g => this.isGroupActive(g));
    if (active) {
      this.activeGroup = active.key;
    }
  }
}
